import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../config/queryKeys'
import {
  getImportSeasons,
  ImportSeason,
  ImportSeasonsPayload,
} from '../../../services/teamBalance'

interface UseImportSeasonsOptions {
  teamId: string
  rosterId: string | null
  /**
   * The one registration whose sheet is open. Null on All Divisions: an import
   * holds slots on ONE sheet, so there is nothing to ask for there.
   */
  registrationId: string | null
}

/**
 * Jeff, Oct 1 — the previous seasons a rep may import players from, newest first
 * (at most five). Drives the first-run choice and the import line under the sheet.
 *
 * The seasons a rep played change rarely, so the answer stays fresh for a while,
 * and it is keyed outside the sheet's tree (see `queryKeys.teamBalanceImport`).
 */
export const useImportSeasons = ({
  teamId,
  rosterId,
  registrationId,
}: UseImportSeasonsOptions) => {
  const enabled =
    Boolean(teamId) && Boolean(rosterId) && Boolean(registrationId)

  const query = useQuery<ImportSeasonsPayload>({
    queryKey: queryKeys.teamBalanceImport.seasons(
      teamId,
      rosterId,
      registrationId
    ),
    queryFn: () =>
      getImportSeasons(teamId, rosterId as string, registrationId as string),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  })

  const seasons: ImportSeason[] = query.data?.seasons ?? []

  return {
    ...query,
    seasons,
    /**
     * No answer yet, and one is on its way: the first load, or a Retry after it
     * failed. A disabled query never is.
     */
    isLoadingSeasons: query.data === undefined && query.isFetching,
  }
}

export default useImportSeasons
